import { API_ORIGIN } from '../config';
const API_BASE_URL = `${API_ORIGIN}/api/inventory`;

class InventoryService {
  getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  getJsonHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  // ── Items ──────────────────────────────────────────────────────────────────

  async getItems() {
    const res = await fetch(`${API_BASE_URL}/items`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async getItem(itemId) {
    const res = await fetch(`${API_BASE_URL}/items/${itemId}`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async getItemByBarcode(barcode) {
    const res = await fetch(`${API_BASE_URL}/items/barcode/${encodeURIComponent(barcode)}`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async createItem(formData) {
    // Content-Type must NOT be set manually for multipart/form-data
    const res = await fetch(`${API_BASE_URL}/items`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: formData,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async updateItem(itemId, formData) {
    const res = await fetch(`${API_BASE_URL}/items/${itemId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: formData,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async deleteItem(itemId) {
    const res = await fetch(`${API_BASE_URL}/items/${itemId}`, {
      method: 'DELETE',
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Transactions ───────────────────────────────────────────────────────────

  async getTransactions(filters = {}) {
    const params = new URLSearchParams();
    if (filters.itemId) params.append('itemId', filters.itemId);
    if (filters.type) params.append('type', filters.type);
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.reservationId) params.append('reservationId', filters.reservationId);
    const url = `${API_BASE_URL}/transactions?${params.toString()}`;
    const res = await fetch(url, { headers: this.getJsonHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async getItemTransactions(itemId) {
    const res = await fetch(`${API_BASE_URL}/transactions/${itemId}`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async adjustStock({ itemId, quantity, transaction_type, notes, from_location_id, to_location_id }) {
    const body = { itemId, quantity, transaction_type, notes };
    if (from_location_id != null) body.from_location_id = from_location_id;
    if (to_location_id != null) body.to_location_id = to_location_id;
    const res = await fetch(`${API_BASE_URL}/adjust`, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Scanners ───────────────────────────────────────────────────────────────

  async registerScanner(data) {
    const res = await fetch(`${API_BASE_URL}/scanners`, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async getScanners() {
    const res = await fetch(`${API_BASE_URL}/scanners`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async updateScanner(scannerId, data) {
    const res = await fetch(`${API_BASE_URL}/scanners/${scannerId}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async deleteScanner(scannerId) {
    const res = await fetch(`${API_BASE_URL}/scanners/${scannerId}`, {
      method: 'DELETE',
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async regenerateScannerToken(scannerId) {
    const res = await fetch(`${API_BASE_URL}/scanners/${scannerId}/regenerate-token`, {
      method: 'POST',
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Locations ──────────────────────────────────────────────────────────────

  async getLocations() {
    const res = await fetch(`${API_BASE_URL}/locations`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async createLocation(data) {
    const res = await fetch(`${API_BASE_URL}/locations`, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async updateLocation(locationId, data) {
    const res = await fetch(`${API_BASE_URL}/locations/${locationId}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async deleteLocation(locationId) {
    const res = await fetch(`${API_BASE_URL}/locations/${locationId}`, {
      method: 'DELETE',
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async approveLocation(locationId) {
    const res = await fetch(`${API_BASE_URL}/locations/${locationId}/approve`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || body.message || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  async declineLocation(locationId, reason) {
    const res = await fetch(`${API_BASE_URL}/locations/${locationId}/decline`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(reason ? { reason } : {}),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || body.message || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  // ── Stock by Location ──────────────────────────────────────────────────────

  async getStockByLocation(filters = {}) {
    const params = new URLSearchParams();
    if (filters.locationId != null) params.set('locationId', filters.locationId);
    if (filters.itemId != null) params.set('itemId', filters.itemId);
    const qs = params.toString();
    const res = await fetch(`${API_BASE_URL}/stock-by-location${qs ? `?${qs}` : ''}`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Public Items (all auth users) ─────────────────────────────────────────

  async getPublicItems() {
    const res = await fetch(`${API_BASE_URL}/public/items`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async getPublicItem(itemId) {
    const res = await fetch(`${API_BASE_URL}/public/items/${itemId}`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // ── Reservations ───────────────────────────────────────────────────────────

  async getMonthAvailability(itemId, year, month, excludeReservationId = null) {
    const params = new URLSearchParams({ item_id: itemId, year, month });
    if (excludeReservationId) params.append('exclude_reservation_id', excludeReservationId);
    const res = await fetch(`${API_BASE_URL}/reservations/availability/month?${params}`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async getReservationLocations() {
    const res = await fetch(`${API_BASE_URL}/reservations/locations`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // FM-creatable destination location (active, non-primary)
  async createReservationLocation(data) {
    const res = await fetch(`${API_BASE_URL}/reservations/locations`, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || body.message || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  // Per-line location split (planning only; works pre- and post-dispatch)
  async getReservationLocationSplit(id) {
    const res = await fetch(`${API_BASE_URL}/reservations/${id}/locations`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async updateReservationLocations(id, locations) {
    const res = await fetch(`${API_BASE_URL}/reservations/${id}/locations`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify({ locations }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || body.message || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  async getAvailability(itemId, startDate, endDate, excludeReservationId = null) {
    const params = new URLSearchParams({ item_id: itemId, start_date: startDate, end_date: endDate });
    if (excludeReservationId) params.append('exclude_reservation_id', excludeReservationId);
    const res = await fetch(`${API_BASE_URL}/reservations/availability?${params}`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async updateReservation(id, data) {
    const res = await fetch(`${API_BASE_URL}/reservations/${id}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || body.message || `HTTP ${res.status}`);
      err.status = res.status;
      err.available_quantity = body.available_quantity ?? null;
      throw err;
    }
    return res.json();
  }

  async getReservationChanges(id) {
    const res = await fetch(`${API_BASE_URL}/reservations/${id}/changes`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async getReservations(params = {}) {
    const qs = params.mine ? '?mine=true' : '';
    const res = await fetch(`${API_BASE_URL}/reservations${qs}`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async createReservation(data) {
    const res = await fetch(`${API_BASE_URL}/reservations`, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || body.message || `HTTP ${res.status}`);
      err.status = res.status;
      err.conflicts = body.conflicts || null;
      throw err;
    }
    return res.json();
  }

  async getMovements(reservationId) {
    const res = await fetch(`${API_BASE_URL}/reservations/${reservationId}/movements`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async createBooking(data) {
    const res = await fetch(`${API_BASE_URL}/reservations`, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(data),
    });
    const body = await res.json().catch(() => ({}));
    if (res.status === 409) {
      const err = new Error(body.error || `HTTP ${res.status}`);
      err.status = 409;
      err.rejected = body.rejected || null;
      throw err;
    }
    if (!res.ok) {
      const err = new Error(body.error || body.message || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return { ...body, partial: res.status === 207 };
  }

  async getBookings(params = {}) {
    const qs = params.mine ? '?mine=true' : '';
    const res = await fetch(`${API_BASE_URL}/reservations/bookings${qs}`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async getBookingPdfBlob(id, download = false) {
    const qs = download ? '?download=1' : '';
    const res = await fetch(`${API_BASE_URL}/reservations/bookings/${id}/pdf${qs}`, {
      headers: this.getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
  }

  async getBooking(id) {
    const res = await fetch(`${API_BASE_URL}/reservations/bookings/${id}`, {
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  async updateBooking(id, data) {
    const res = await fetch(`${API_BASE_URL}/reservations/bookings/${id}`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || body.message || `HTTP ${res.status}`);
      err.status = res.status;
      err.failed = body.failed || null;
      throw err;
    }
    return res.json();
  }

  async cancelBooking(id) {
    const res = await fetch(`${API_BASE_URL}/reservations/bookings/${id}/cancel`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || body.message || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  async cancelReservation(id) {
    const res = await fetch(`${API_BASE_URL}/reservations/${id}/cancel`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || body.message || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  async dispatch(id, data = {}) {
    const res = await fetch(`${API_BASE_URL}/reservations/${id}/dispatch`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || body.message || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  async returnReservation(id, data = {}) {
    const res = await fetch(`${API_BASE_URL}/reservations/${id}/return`, {
      method: 'PUT',
      headers: this.getJsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || body.message || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  // ── Live Updates (SSE) ────────────────────────────────────────────────────
  // EventSource can't send custom headers, so the bearer token this service
  // uses everywhere else is passed as a query param instead of relying on
  // cookie auth (which isn't guaranteed across environments).

  subscribeToEvents({ onItem, onBooking, onLocation } = {}) {
    const token = localStorage.getItem('authToken');
    const url = `${API_BASE_URL}/events${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    const es = new EventSource(url);
    es.addEventListener('inventory', (e) => {
      let payload;
      try {
        payload = JSON.parse(e.data);
      } catch {
        return;
      }
      if (payload?.type === 'item') onItem?.(payload);
      else if (payload?.type === 'booking') onBooking?.(payload);
      else if (payload?.type === 'location') onLocation?.(payload);
    });
    return es;
  }
}

export default new InventoryService();
